/**
 * Local file backup operations
 */

import type { KeyshareBackupData } from '../types'
import { deriveBackupPasswordFromPasskey, encryptKeyshare } from './crypto-utils'

/**
 * Backup keyshare to local file (download as JSON)
 */
export async function backupToLocalFile(data: KeyshareBackupData, password?: string): Promise<void> {
  let encryptionPassword = password
  let credentialId: string | undefined

  if (!encryptionPassword) {
    const result = await deriveBackupPasswordFromPasskey(data.userId)
    encryptionPassword = result.password
    credentialId = result.credentialId
  }

  const encrypted = await encryptKeyshare(data, encryptionPassword, password ? 'password' : 'passkey', credentialId)

  const backupData = {
    ...encrypted,
    userId: data.userId,
    createdAt: data.createdAt
  }

  const blob = new Blob([JSON.stringify(backupData, null, 2)], {
    type: 'application/json'
  })

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `embarkai-backup-${data.userId}-${Date.now()}.json`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

/**
 * Restore keyshare from local file
 */
export async function restoreFromLocalFile(
  fileContent: string,
  userId: string,
  password?: string
): Promise<KeyshareBackupData> {
  console.log('[iframe][restoreFromLocalFile] Starting restore from local file')

  const encryptedBackup = JSON.parse(fileContent)

  let decryptionPassword: string
  let credentialId: string | undefined

  if (password) {
    // Use provided password
    decryptionPassword = password
  } else {
    // Use passkey-derived password, with specific credentialId if available
    const credentialIdFromBackup =
      encryptedBackup.encryptionMethod === 'passkey' ? encryptedBackup.credentialId : undefined
    const result = await deriveBackupPasswordFromPasskey(userId, credentialIdFromBackup).catch(() => {
      throw new Error('Restore requires either password or passkey authentication')
    })
    decryptionPassword = result.password
    credentialId = result.credentialId
  }

  // Import decrypt function
  const { decryptKeyshare } = await import('./crypto-utils')
  const backupData = await decryptKeyshare(encryptedBackup, decryptionPassword)

  if (backupData.userId !== userId) {
    throw new Error('Backup file does not match current user')
  }

  console.log('[iframe][restoreFromLocalFile] Restore successful')
  return backupData
}
